/**
 * Programmatic SEO helper — generates unique content for thousands of pages.
 * Each page variant has different H1, title, description, intro text, and FAQ.
 */
import { formatCityName } from './city-format'

// ─── Service name mappings (slug → French labels) ────────────

export const SERVICE_LABELS: Record<string, { name: string; artisan: string; article: string }> = {
  plomberie: { name: 'Plomberie', artisan: 'plombier', article: 'un' },
  electricite: { name: 'Électricité', artisan: 'électricien', article: 'un' },
  peinture: { name: 'Peinture', artisan: 'peintre', article: 'un' },
  renovation: { name: 'Rénovation', artisan: 'artisan rénovation', article: 'un' },
  menuiserie: { name: 'Menuiserie', artisan: 'menuisier', article: 'un' },
  toiture: { name: 'Toiture', artisan: 'couvreur', article: 'un' },
  serrurerie: { name: 'Serrurerie', artisan: 'serrurier', article: 'un' },
  nettoyage: { name: 'Nettoyage', artisan: 'agent de nettoyage', article: 'un' },
  jardinage: { name: 'Jardinage', artisan: 'jardinier', article: 'un' },
  climatisation: { name: 'Climatisation', artisan: 'climaticien', article: 'un' },
  demenagement: { name: 'Déménagement', artisan: 'déménageur', article: 'un' },
  'carrelage-sol': { name: 'Carrelage', artisan: 'carreleur', article: 'un' },
  isolation: { name: 'Isolation', artisan: 'isolateur', article: 'un' },
  maconnerie: { name: 'Maçonnerie', artisan: 'maçon', article: 'un' },
  platrerie: { name: 'Plâtrerie', artisan: 'plâtrier', article: 'un' },
  'couverture-zinguerie': { name: 'Couverture', artisan: 'couvreur-zingueur', article: 'un' },
  piscine: { name: 'Piscine', artisan: 'pisciniste', article: 'un' },
  securite: { name: 'Sécurité', artisan: 'installateur sécurité', article: 'un' },
  'panneaux-solaires': { name: 'Panneaux solaires', artisan: 'installateur solaire', article: 'un' },
  assainissement: { name: 'Assainissement', artisan: 'technicien assainissement', article: 'un' },
  vitrerie: { name: 'Vitrerie', artisan: 'vitrier', article: 'un' },
  chauffage: { name: 'Chauffage', artisan: 'chauffagiste', article: 'un' },
  terrassement: { name: 'Terrassement', artisan: 'terrassier', article: 'un' },
  ramonage: { name: 'Ramonage', artisan: 'ramoneur', article: 'un' },
  parquet: { name: 'Parquet', artisan: 'parqueteur', article: 'un' },
  'stores-volets': { name: 'Stores et volets', artisan: 'installateur stores', article: 'un' },
  plaquiste: { name: 'Plaquiste', artisan: 'plaquiste', article: 'un' },
  charpente: { name: 'Charpente', artisan: 'charpentier', article: 'un' },
  ferronnerie: { name: 'Ferronnerie', artisan: 'ferronnier', article: 'un' },
  facade: { name: 'Façade', artisan: 'façadier', article: 'un' },
}

// Service prices for content
export const SERVICE_PRICES: Record<string, { min: number; max: number; unit: string }> = {
  plomberie: { min: 80, max: 500, unit: 'intervention' },
  electricite: { min: 150, max: 1500, unit: 'projet' },
  peinture: { min: 20, max: 45, unit: 'm²' },
  renovation: { min: 5000, max: 25000, unit: 'projet' },
  menuiserie: { min: 200, max: 3000, unit: 'projet' },
  toiture: { min: 500, max: 5000, unit: 'projet' },
  serrurerie: { min: 80, max: 400, unit: 'intervention' },
  nettoyage: { min: 20, max: 50, unit: 'heure' },
  jardinage: { min: 30, max: 80, unit: 'heure' },
  climatisation: { min: 500, max: 3000, unit: 'installation' },
  demenagement: { min: 300, max: 3000, unit: 'déménagement' },
  'carrelage-sol': { min: 25, max: 70, unit: 'm²' },
  isolation: { min: 30, max: 120, unit: 'm²' },
  maconnerie: { min: 500, max: 5000, unit: 'projet' },
  vitrerie: { min: 80, max: 500, unit: 'intervention' },
  chauffage: { min: 500, max: 5000, unit: 'installation' },
  terrassement: { min: 1000, max: 10000, unit: 'projet' },
  ramonage: { min: 50, max: 120, unit: 'ramonage' },
  parquet: { min: 25, max: 80, unit: 'm²' },
  'stores-volets': { min: 200, max: 1500, unit: 'installation' },
  plaquiste: { min: 25, max: 60, unit: 'm²' },
  charpente: { min: 1000, max: 15000, unit: 'projet' },
  ferronnerie: { min: 500, max: 5000, unit: 'projet' },
  facade: { min: 30, max: 120, unit: 'm²' },
}

/**
 * Top 12 French cities by population — used for hub-spoke internal linking
 * from category hubs (/services/*) to long-tail prix/urgence pages. Format:
 * { name: display, slug: URL slug }. citySlug() output is the same as `.slug`.
 */
export const TOP_CITIES: Array<{ name: string; slug: string }> = [
  { name: 'Paris', slug: 'paris' },
  { name: 'Marseille', slug: 'marseille' },
  { name: 'Lyon', slug: 'lyon' },
  { name: 'Toulouse', slug: 'toulouse' },
  { name: 'Nice', slug: 'nice' },
  { name: 'Nantes', slug: 'nantes' },
  { name: 'Strasbourg', slug: 'strasbourg' },
  { name: 'Montpellier', slug: 'montpellier' },
  { name: 'Bordeaux', slug: 'bordeaux' },
  { name: 'Lille', slug: 'lille' },
  { name: 'Rennes', slug: 'rennes' },
  { name: 'Reims', slug: 'reims' },
]

/**
 * Slugify a service artisan name for /prix/{artisan}-{city} URL pattern.
 * "plombier" → "plombier", "carreleur" → "carreleur".
 */
export function artisanSlug(serviceSlug: string): string {
  const svc = SERVICE_LABELS[serviceSlug]
  if (!svc) return serviceSlug
  return svc.artisan
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

// Emergency services
export const EMERGENCY_SERVICES = [
  'plomberie', 'electricite', 'serrurerie', 'toiture',
  'climatisation', 'assainissement', 'couverture-zinguerie', 'nettoyage',
]

// Curated Unsplash trade photos. Used as fallback artisan-card thumbnails on
// programmatic /prix pages until real per-pro photos populate the DB.
// Same image per trade keeps brand consistent and CDN cache hot.
export const SERVICE_PHOTOS: Record<string, string> = {
  plomberie: 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39',
  electricite: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a',
  peinture: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828',
  renovation: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12',
  menuiserie: 'https://images.unsplash.com/photo-1504148455328-c376907d081c',
  toiture: 'https://images.unsplash.com/photo-1632823469850-1b7b1e8b7e44',
  'couverture-zinguerie': 'https://images.unsplash.com/photo-1632823469850-1b7b1e8b7e44',
  serrurerie: 'https://images.unsplash.com/photo-1582139329536-e7284fece509',
  nettoyage: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952',
  jardinage: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b',
  climatisation: 'https://images.unsplash.com/photo-1581094288338-2314dddb7ece',
  demenagement: 'https://images.unsplash.com/photo-1600518464441-9154a4dea21b',
  'carrelage-sol': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136',
  isolation: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12',
  maconnerie: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e',
  platrerie: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828',
  piscine: 'https://images.unsplash.com/photo-1572331165267-854da2b10ccc',
  securite: 'https://images.unsplash.com/photo-1558002038-1055907df827',
  'panneaux-solaires': 'https://images.unsplash.com/photo-1509391366360-2e959784a276',
  assainissement: 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39',
  chauffage: 'https://images.unsplash.com/photo-1581094288338-2314dddb7ece',
  facade: 'https://images.unsplash.com/photo-1605146769289-440113cc3d00',
  charpente: 'https://images.unsplash.com/photo-1504148455328-c376907d081c',
  ferronnerie: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e',
}

// ─── Page variant generators ─────────────────────────────────

export type PageVariant = 'prix' | 'trouver' | 'devis' | 'urgence' | 'tarif'

export function generatePageContent(variant: PageVariant, serviceSlug: string, city?: string) {
  const svc = SERVICE_LABELS[serviceSlug] ?? { name: serviceSlug, artisan: serviceSlug, article: 'un' }
  const price = SERVICE_PRICES[serviceSlug]
  const cityText = city ?? 'votre ville'

  const Cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
  const year = new Date().getFullYear()
  const priceRange = price ? `${price.min}-${price.max}€` : ''
  const priceUnitFr = price ? `${price.min}-${price.max}€/${price.unit}` : ''

  switch (variant) {
    case 'prix':
      // CTR-tuned title: keyword + city + year + concrete price → strong commercial intent match.
      // Number in title increases CTR ~15-20% per Backlinko 2024 SERP CTR study.
      return {
        title: price
          ? `Prix ${svc.artisan} ${cityText} ${year} : ${priceRange} | Devis gratuit 48h`
          : `Prix ${svc.artisan} ${cityText} ${year} | Devis gratuit 48h`,
        description: price
          ? `${Cap(svc.artisan)} à ${cityText} : tarifs ${priceRange} en ${year}. ✓ 3 devis gratuits sous 48h ✓ Artisans SIRET vérifiés + assurés ✓ Avis clients. Économisez jusqu'à 40 %.`
          : `${Cap(svc.artisan)} à ${cityText} : tarifs ${year} détaillés. ✓ 3 devis gratuits sous 48h ✓ Artisans SIRET vérifiés + assurés ✓ Avis clients. Économisez jusqu'à 40 %.`,
        h1: `Prix ${svc.artisan} à ${cityText} en ${year}`,
        intro: `Vous cherchez à connaître le prix d'${svc.article} ${svc.artisan} à ${cityText} ? Les tarifs varient ${price ? `de ${price.min} € à ${price.max} € par ${price.unit}` : 'selon la complexité du projet'} en fonction du type de travaux, de la difficulté et de votre localisation.`,
        cta: `Comparer les prix à ${cityText}`,
      }
    case 'trouver':
      return {
        title: `Trouver ${svc.article} ${svc.artisan} à ${cityText} — Artisans vérifiés`,
        description: `Trouvez ${svc.article} ${svc.artisan} de confiance à ${cityText}. Artisans vérifiés, avis clients, devis gratuits sous 48h.`,
        h1: `Trouver ${svc.article} ${svc.artisan} à ${cityText}`,
        intro: `Besoin d'${svc.article} ${svc.artisan} fiable à ${cityText} ? Sur AfficheServices, tous les professionnels sont vérifiés : SIRET valide, assurance RC Pro, identité contrôlée. Comparez les avis et choisissez le meilleur artisan.`,
        cta: `Voir les ${svc.artisan}s à ${cityText}`,
      }
    case 'devis':
      return {
        title: `Devis ${svc.name.toLowerCase()} à ${cityText} — Gratuit sous 48h`,
        description: `Demandez un devis ${svc.name.toLowerCase()} gratuit à ${cityText}. Jusqu'à 3 devis de professionnels vérifiés sous 48h. Sans engagement.`,
        h1: `Devis ${svc.name.toLowerCase()} gratuit à ${cityText}`,
        intro: `Obtenez jusqu'à 3 devis gratuits pour vos travaux de ${svc.name.toLowerCase()} à ${cityText}. Des artisans vérifiés et assurés vous contactent sous 48 heures avec leurs propositions. Service 100% gratuit et sans engagement.`,
        cta: `Demander mes devis gratuits`,
      }
    case 'urgence':
      // Urgency hooks: "sous 1h", "24/7", "soir & weekend". Convert higher.
      return {
        title: `${Cap(svc.artisan)} urgence ${cityText} 24/7 — Intervention sous 1h`,
        description: `${Cap(svc.artisan)} en urgence à ${cityText} ✓ Intervention sous 1h ✓ 24h/24 7j/7 ✓ Soir & week-end ✓ Devis sur place ✓ Artisans assurés. Demande immédiate par formulaire.`,
        h1: `${Cap(svc.artisan)} en urgence à ${cityText}`,
        intro: `Besoin d'${svc.article} ${svc.artisan} en urgence à ${cityText} ? Nos professionnels interviennent rapidement, y compris le soir et le week-end. Tous sont vérifiés et assurés pour votre tranquillité.`,
        cta: `Intervention urgente à ${cityText}`,
      }
    case 'tarif':
      return {
        title: price
          ? `Tarif ${svc.artisan} ${year} : ${priceUnitFr} | Grille complète`
          : `Tarif ${svc.artisan} ${year} — Grille de prix complète`,
        description: `Tarifs ${svc.artisan} ${year} : ${price ? priceUnitFr : 'tous les prix'}. Grille détaillée, facteurs de prix, erreurs à éviter, garanties. ✓ Devis gratuit ✓ Économisez jusqu'à 40 %.`,
        h1: `Tarif ${svc.artisan} en ${year}`,
        intro: `Combien coûte ${svc.article} ${svc.artisan} en France en ${year} ? Découvrez notre grille tarifaire complète basée sur les prix réels du marché, les facteurs qui influencent le coût, et nos conseils pour obtenir le meilleur rapport qualité-prix.`,
        cta: `Obtenir un devis personnalisé`,
      }
  }
}

// ─── Parse combined slug (e.g., "plombier-paris") ────────────

export function parseServiceCitySlug(combined: string): { serviceSlug: string; cityName: string } | null {
  // Reject absurdly long slugs (bot spam like "zingueur-zingueur-zingueur..." repeated 100x)
  if (combined.length > 100) return null

  // Sort by artisan name length DESC to match "couvreur-zingueur" before "couvreur"
  const sortedLabels = Object.entries(SERVICE_LABELS).sort((a, b) => {
    const aSlug = a[1].artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    const bSlug = b[1].artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    return bSlug.length - aSlug.length
  })

  // Try all known service artisan names (longest first)
  for (const [slug, svc] of sortedLabels) {
    const artisanSlug = svc.artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    if (combined.startsWith(artisanSlug + '-')) {
      const cityPart = combined.slice(artisanSlug.length + 1)
      if (!cityPart || cityPart.length > 50) return null
      const cityName = formatCityName(cityPart)
      return { serviceSlug: slug, cityName }
    }
  }

  // Fallback: try service name slug (also sorted by length)
  const sortedByName = Object.entries(SERVICE_LABELS).sort((a, b) => {
    const aSlug = a[1].name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    const bSlug = b[1].name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    return bSlug.length - aSlug.length
  })

  for (const [slug, svc] of sortedByName) {
    const nameSlug = svc.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    if (combined.startsWith(nameSlug + '-')) {
      const cityPart = combined.slice(nameSlug.length + 1)
      if (!cityPart || cityPart.length > 50) return null
      const cityName = formatCityName(cityPart)
      return { serviceSlug: slug, cityName }
    }
  }

  return null
}
